import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { BlogPost, BlogCategory } from '../../types/blog';
import { SuccessCase } from '../../types/successCase';
import AdminBlogTable from './blog/components/AdminBlogTable';
import BlogFormModal from './blog/components/BlogFormModal';
import AdminCategorySection from './blog/components/AdminCategorySection';
import SuccessCaseTable from './success/components/SuccessCaseTable';
import SuccessCaseFormModal from './success/components/SuccessCaseFormModal';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import AdminLoginPage from './components/AdminLoginPage';
import HelpModal, { HelpStep } from '../../components/feature/HelpModal';

const ADMIN_NAV_HELP: HelpStep[] = [
  {
    icon: 'ri-dashboard-line',
    title: '管理画面の構成',
    description: '管理画面は「ブログ管理」と「成婚事例管理」の2つのセクションで構成されています。上部のタブで切り替えて使用します。',
  },
  {
    icon: 'ri-article-line',
    title: 'ブログ管理',
    description: '「ブログ管理」タブでは、記事一覧とカテゴリー管理ができます。右上の「記事を追加」ボタンから新しい記事を作成できます。',
    tips: ['記事一覧で状態の切り替え・編集・削除ができます'],
  },
  {
    icon: 'ri-heart-line',
    title: '成婚事例管理',
    description: '「成婚事例管理」タブでは、成婚事例の追加・編集・削除ができます。ドラッグ&ドロップで表示順も変更できます。',
    tips: ['右上の「事例を追加」ボタンから新しい成婚事例を登録できます'],
  },
  {
    icon: 'ri-external-link-line',
    title: '公開ページを確認する',
    description: '右上の「ブログを見る」または「成婚事例を見る」ボタンから、実際に公開されているページを別タブで確認できます。',
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: 'ダッシュボードの数字',
    description: '画面上部にある4つの数字は、総記事数・公開中の記事数・成婚事例数・公開中の事例数をリアルタイムで表示しています。',
  },
  {
    icon: 'ri-logout-box-r-line',
    title: 'ログアウト',
    description: '右上の「ログアウト」ボタンをクリックするとセッションが終了しログイン画面に戻ります。',
    tips: ['作業後は必ずログアウトしてください'],
  },
];

type MainTab = 'blog' | 'success';
type BlogSubTab = 'posts' | 'categories';

export default function AdminPage() {
  const { isAuthenticated, loading: authLoading, login, logout, error: authError } = useAdminAuth();

  // Blog state
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogModalOpen, setBlogModalOpen] = useState(false);
  const [editPost, setEditPost] = useState<BlogPost | null>(null);
  const [blogSubTab, setBlogSubTab] = useState<BlogSubTab>('posts');

  // Success cases state
  const [cases, setCases] = useState<SuccessCase[]>([]);
  const [casesLoading, setCasesLoading] = useState(true);
  const [caseModalOpen, setCaseModalOpen] = useState(false);
  const [editCase, setEditCase] = useState<SuccessCase | null>(null);

  // Shared state
  const [mainTab, setMainTab] = useState<MainTab>('blog');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [navHelpOpen, setNavHelpOpen] = useState(false);

  // Image copy state (static images)
  const [imageCopyLoading, setImageCopyLoading] = useState(false);
  const [imageCopyResult, setImageCopyResult] = useState<{
    summary: { total: number; success: number; failed: number };
    results: { name: string; status: string; s3Url?: string; error?: string }[];
  } | null>(null);

  // DB image migration state
  const [dbMigrateLoading, setDbMigrateLoading] = useState(false);
  const [dbMigrateResult, setDbMigrateResult] = useState<{
    summary: { total: number; success: number; failed: number };
    results: { table: string; id: string; status: string; oldUrl?: string; newUrl?: string; error?: string }[];
  } | null>(null);

  const handleCopyImages = async () => {
    setImageCopyLoading(true);
    setImageCopyResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('copy-images-to-storage');
      if (error) throw error;
      setImageCopyResult(data);
      if (data.summary.failed === 0) {
        showToast('success', `${data.summary.success}枚の画像をS3にコピーしました！`);
      } else {
        showToast('error', `${data.summary.success}枚成功、${data.summary.failed}枚失敗しました。`);
      }
    } catch (err) {
      showToast('error', '画像コピーに失敗しました。');
    } finally {
      setImageCopyLoading(false);
    }
  };

  const handleMigrateDbImages = async () => {
    setDbMigrateLoading(true);
    setDbMigrateResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('migrate-db-images');
      if (error) throw error;
      setDbMigrateResult(data);
      if (data.summary.failed === 0) {
        showToast('success', `${data.summary.success}件の画像をS3に移行しました！`);
      } else {
        showToast('error', `${data.summary.success}件成功、${data.summary.failed}件失敗しました。`);
      }
    } catch (err) {
      showToast('error', 'DB画像の移行に失敗しました。');
    } finally {
      setDbMigrateLoading(false);
    }
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch blog data
  const fetchBlogData = useCallback(async () => {
    setBlogLoading(true);
    const [{ data: cats }, { data: postsData }] = await Promise.all([
      supabase.from('8a4f04ed_blog_categories').select('*').order('created_at'),
      supabase
        .from('8a4f04ed_blog_posts')
        .select('*, category:8a4f04ed_blog_categories(*)')
        .order('published_at', { ascending: false }),
    ]);
    if (cats) setCategories(cats);
    if (postsData) setPosts(postsData as BlogPost[]);
    setBlogLoading(false);
  }, []);

  // Fetch success cases
  const fetchCases = useCallback(async () => {
    setCasesLoading(true);
    const { data } = await supabase
      .from('8a4f04ed_success_cases')
      .select('*')
      .order('sort_order', { ascending: true });
    if (data) setCases(data as SuccessCase[]);
    setCasesLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBlogData();
      fetchCases();
    }
  }, [fetchBlogData, fetchCases, isAuthenticated]);

  // Blog handlers
  const handleBlogEdit = (post: BlogPost) => { setEditPost(post); setBlogModalOpen(true); };
  const handleBlogAdd = () => { setEditPost(null); setBlogModalOpen(true); };
  const handleBlogDelete = async (id: string) => {
    const { error } = await supabase.from('8a4f04ed_blog_posts').delete().eq('id', id);
    if (error) showToast('error', '削除に失敗しました。');
    else { showToast('success', '記事を削除しました。'); fetchBlogData(); }
  };
  const handleBlogSaved = () => {
    showToast('success', editPost ? '記事を更新しました。' : '記事を追加しました。');
    fetchBlogData();
  };
  const handleBlogStatusToggle = async (id: string, current: 'published' | 'draft') => {
    const next = current === 'published' ? 'draft' : 'published';
    const { error } = await supabase
      .from('8a4f04ed_blog_posts')
      .update({ status: next, published: next === 'published', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) showToast('error', 'ステータスの更新に失敗しました。');
    else { showToast('success', next === 'published' ? '記事を公開しました。' : '下書きに変更しました。'); fetchBlogData(); }
  };

  // Success case handlers
  const handleCaseEdit = (item: SuccessCase) => { setEditCase(item); setCaseModalOpen(true); };
  const handleCaseAdd = () => { setEditCase(null); setCaseModalOpen(true); };
  const handleCaseDelete = async (id: string) => {
    const { error } = await supabase.from('8a4f04ed_success_cases').delete().eq('id', id);
    if (error) showToast('error', '削除に失敗しました。');
    else { showToast('success', '成婚事例を削除しました。'); fetchCases(); }
  };
  const handleCaseSaved = () => {
    showToast('success', editCase ? '成婚事例を更新しました。' : '成婚事例を追加しました。');
    fetchCases();
  };
  const handleCaseStatusToggle = async (id: string, current: 'published' | 'draft') => {
    const next = current === 'published' ? 'draft' : 'published';
    const { error } = await supabase
      .from('8a4f04ed_success_cases')
      .update({ status: next, published: next === 'published', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) showToast('error', 'ステータスの更新に失敗しました。');
    else { showToast('success', next === 'published' ? '公開しました。' : '下書きに変更しました。'); fetchCases(); }
  };

  const handleCaseReorder = async (reordered: SuccessCase[]) => {
    setCases(reordered);
    try {
      await Promise.all(
        reordered.map((item) =>
          supabase
            .from('8a4f04ed_success_cases')
            .update({ sort_order: item.sort_order, updated_at: new Date().toISOString() })
            .eq('id', item.id)
        )
      );
      showToast('success', '並び順を保存しました。');
    } catch {
      showToast('error', '並び順の保存に失敗しました。');
      fetchCases();
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#fff8f2' }}>
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: '#5a9e6f' }}></i>
        </span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLoginPage onLogin={login} error={authError} />;
  }

  const nextCaseSortOrder = cases.length > 0 ? Math.max(...cases.map((c) => c.sort_order)) + 1 : 1;

  return (
    <div className="min-h-screen" style={{ background: '#fff8f2' }}>
      <HelpModal
        title="管理画面の使い方"
        sections={[{ label: '機能説明', steps: ADMIN_NAV_HELP }]}
        isOpen={navHelpOpen}
        onClose={() => setNavHelpOpen(false)}
      />
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-6 right-6 z-[100] px-5 py-3 rounded-xl text-sm font-bold"
          style={{ background: toast.type === 'success' ? '#5a9e6f' : '#cc4444', color: '#fff' }}
        >
          {toast.message}
        </div>
      )}

      {/* Top Bar */}
      <div
        className="sticky top-0 z-40 px-6 h-[64px] flex items-center justify-between"
        style={{ background: '#fff', borderBottom: '2px solid #5a9e6f' }}
      >
        <div className="flex items-center gap-4">
          <Link to="/" style={{ color: '#8b4513', fontWeight: '700', fontSize: '18px' }} className="whitespace-nowrap">
            縁結び 和はは
          </Link>
          <span style={{ color: '#d4c4ba' }}>|</span>
          <h1 style={{ color: '#4a3728', fontWeight: '700', fontSize: '16px' }} className="whitespace-nowrap">
            管理画面
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {mainTab === 'blog' && (
            <Link
              to="/blog"
              target="_blank"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 whitespace-nowrap"
              style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-external-link-line text-sm"></i>
              </span>
              ブログを見る
            </Link>
          )}
          {mainTab === 'success' && (
            <Link
              to="/success"
              target="_blank"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 whitespace-nowrap"
              style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-external-link-line text-sm"></i>
              </span>
              成婚事例を見る
            </Link>
          )}
          {mainTab === 'blog' && blogSubTab === 'posts' && (
            <button
              onClick={handleBlogAdd}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: '#5a9e6f' }}
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-add-line text-sm"></i>
              </span>
              記事を追加
            </button>
          )}
          {mainTab === 'success' && (
            <button
              onClick={handleCaseAdd}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: '#e8934e' }}
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-add-line text-sm"></i>
              </span>
              事例を追加
            </button>
          )}
          <button
            onClick={() => setNavHelpOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#fff0d8', color: '#c4903a', border: '1px solid #f0d8a0' }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-question-line text-sm"></i>
            </span>
            使い方
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#fdf0ee', color: '#cc4444', border: '1px solid #f0c8c0' }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-logout-box-r-line text-sm"></i>
            </span>
            ログアウト
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>総記事数</p>
            <p className="text-2xl font-bold" style={{ color: '#4a3728' }}>{posts.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>公開中の記事</p>
            <p className="text-2xl font-bold" style={{ color: '#5a9e6f' }}>{posts.filter((p) => p.status !== 'draft').length}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>成婚事例数</p>
            <p className="text-2xl font-bold" style={{ color: '#e8934e' }}>{cases.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>公開中の事例</p>
            <p className="text-2xl font-bold" style={{ color: '#8b4513' }}>{cases.filter((c) => c.status === 'published').length}</p>
          </div>
        </div>

        {/* Main Tab Switcher */}
        <div className="inline-flex rounded-full p-1 mb-6" style={{ background: '#f0e8e0' }}>
          <button
            onClick={() => setMainTab('blog')}
            className="flex items-center gap-1.5 px-6 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: mainTab === 'blog' ? '#fff' : 'transparent',
              color: mainTab === 'blog' ? '#4a3728' : '#9b8b7f',
              boxShadow: mainTab === 'blog' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-article-line text-sm"></i>
            </span>
            ブログ管理
          </button>

          <button
            onClick={() => setMainTab('success')}
            className="flex items-center gap-1.5 px-6 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: mainTab === 'success' ? '#fff' : 'transparent',
              color: mainTab === 'success' ? '#4a3728' : '#9b8b7f',
              boxShadow: mainTab === 'success' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-heart-line text-sm"></i>
            </span>
            成婚事例管理
          </button>
        </div>

        {/* Blog Tab Content */}
        {mainTab === 'blog' && (
          <>
            {/* Blog Sub Tab */}
            <div className="inline-flex rounded-full p-1 mb-5" style={{ background: '#f7f2ee' }}>
              <button
                onClick={() => setBlogSubTab('posts')}
                className="flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: blogSubTab === 'posts' ? '#fff' : 'transparent',
                  color: blogSubTab === 'posts' ? '#4a3728' : '#9b8b7f',
                  boxShadow: blogSubTab === 'posts' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-file-text-line text-sm"></i>
                </span>
                記事一覧
              </button>
              <button
                onClick={() => setBlogSubTab('categories')}
                className="flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: blogSubTab === 'categories' ? '#fff' : 'transparent',
                  color: blogSubTab === 'categories' ? '#4a3728' : '#9b8b7f',
                  boxShadow: blogSubTab === 'categories' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                }}
              >
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-folder-line text-sm"></i>
                </span>
                カテゴリー管理
              </button>
            </div>

            {blogSubTab === 'posts' ? (
              <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
                <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #e8d8cc' }}>
                  <h2 className="font-bold" style={{ color: '#4a3728', fontSize: '15px' }}>記事一覧</h2>
                  <span style={{ color: '#9b8b7f', fontSize: '13px' }}>{posts.length}件</span>
                </div>
                {blogLoading ? (
                  <div className="flex items-center justify-center py-20 gap-3" style={{ color: '#9b8b7f' }}>
                    <span className="flex items-center justify-center w-5 h-5">
                      <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#5a9e6f' }}></i>
                    </span>
                    読み込み中...
                  </div>
                ) : (
                  <AdminBlogTable
                    posts={posts}
                    categories={categories}
                    onEdit={handleBlogEdit}
                    onDelete={handleBlogDelete}
                    onStatusToggle={handleBlogStatusToggle}
                  />
                )}
              </div>
            ) : (
              <AdminCategorySection
                categories={categories}
                posts={posts}
                onRefresh={fetchBlogData}
                onToast={showToast}
              />
            )}
          </>
        )}

        {/* Settings Tab Content */}
        {mainTab === 'settings' && (
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
            <div className="px-6 py-4" style={{ borderBottom: '1px solid #e8d8cc' }}>
              <h2 className="font-bold" style={{ color: '#4a3728', fontSize: '15px' }}>サイト設定</h2>
            </div>
            <div className="p-6 flex flex-col gap-5">

              {/* Static Image Copy Section */}
              <div className="rounded-xl p-5" style={{ background: '#fdf8f4', border: '1px solid #e8d8cc' }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: '#f0e8e0' }}>
                    <i className="ri-image-line text-lg" style={{ color: '#8b4513' }}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-1" style={{ color: '#4a3728' }}>サイト静的画像をS3にコピー</h3>
                    <p className="text-xs" style={{ color: '#9b8b7f' }}>
                      サイト固定画像（ヒーロー画像・フロー画像など）を
                      <code className="px-1 py-0.5 rounded text-xs mx-0.5" style={{ background: '#f0e8e0', color: '#8b4513' }}>https://media.genaiweb.ai/8a4f04ed-3960-472b-978d-5363f01a3123/</code>
                      にコピーします。
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCopyImages}
                  disabled={imageCopyLoading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#8b4513' }}
                >
                  {imageCopyLoading ? (
                    <>
                      <span className="flex items-center justify-center w-4 h-4">
                        <i className="ri-loader-4-line text-sm animate-spin"></i>
                      </span>
                      コピー中...
                    </>
                  ) : (
                    <>
                      <span className="flex items-center justify-center w-4 h-4">
                        <i className="ri-upload-cloud-line text-sm"></i>
                      </span>
                      静的画像をコピーする（19枚）
                    </>
                  )}
                </button>

                {imageCopyResult && (
                  <div className="mt-4">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#e8f5ee', color: '#5a9e6f' }}>
                        成功 {imageCopyResult.summary.success}枚
                      </span>
                      {imageCopyResult.summary.failed > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#fdecea', color: '#cc4444' }}>
                          失敗 {imageCopyResult.summary.failed}枚
                        </span>
                      )}
                    </div>
                    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ background: '#f7f2ee' }}>
                            <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>ファイル名</th>
                            <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>ステータス</th>
                            <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>S3 URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {imageCopyResult.results.map((r, i) => (
                            <tr key={i} style={{ borderTop: '1px solid #f0e8e0' }}>
                              <td className="px-3 py-2 font-mono" style={{ color: '#4a3728' }}>{r.name}</td>
                              <td className="px-3 py-2">
                                {r.status === 'success' ? (
                                  <span className="font-bold" style={{ color: '#5a9e6f' }}>✓ 成功</span>
                                ) : (
                                  <span className="font-bold" style={{ color: '#cc4444' }}>✗ {r.error}</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {r.s3Url && (
                                  <a href={r.s3Url} target="_blank" rel="nofollow noreferrer" className="underline hover:opacity-70" style={{ color: '#8b4513' }}>開く</a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* DB Image Migration Section */}
              <div className="rounded-xl p-5" style={{ background: '#fdf8f4', border: '1px solid #e8d8cc' }}>
                <div className="flex items-start gap-4 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl" style={{ background: '#f0e8e0' }}>
                    <i className="ri-database-2-line text-lg" style={{ color: '#5a9e6f' }}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-sm mb-1" style={{ color: '#4a3728' }}>DB登録画像をS3に移行</h3>
                    <p className="text-xs" style={{ color: '#9b8b7f' }}>
                      ブログ記事・成婚事例のサムネイルなど、Supabase DBに登録画像を
                      S3にコピーし、DBのURLを自動で書き換えます。
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleMigrateDbImages}
                  disabled={dbMigrateLoading}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#5a9e6f' }}
                >
                  {dbMigrateLoading ? (
                    <>
                      <span className="flex items-center justify-center w-4 h-4">
                        <i className="ri-loader-4-line text-sm animate-spin"></i>
                      </span>
                      移行中...
                    </>
                  ) : (
                    <>
                      <span className="flex items-center justify-center w-4 h-4">
                        <i className="ri-refresh-line text-sm"></i>
                      </span>
                      DB画像をS3に移行する
                    </>
                  )}
                </button>

                {dbMigrateResult && (
                  <div className="mt-4">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#e8f5ee', color: '#5a9e6f' }}>
                        成功 {dbMigrateResult.summary.success}件
                      </span>
                      {dbMigrateResult.summary.failed > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#fdecea', color: '#cc4444' }}>
                          失敗 {dbMigrateResult.summary.failed}件
                        </span>
                      )}
                      {dbMigrateResult.summary.total === 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#f0e8e0', color: '#9b8b7f' }}>
                          移行対象なし（すでに完了済み）
                        </span>
                      )}
                    </div>
                    {dbMigrateResult.results.length > 0 && (
                      <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr style={{ background: '#f7f2ee' }}>
                              <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>テーブル</th>
                              <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>ステータス</th>
                              <th className="text-left px-3 py-2 font-bold" style={{ color: '#9b8b7f' }}>新URL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dbMigrateResult.results.map((r, i) => (
                              <tr key={i} style={{ borderTop: '1px solid #f0e8e0' }}>
                                <td className="px-3 py-2 font-mono" style={{ color: '#4a3728' }}>{r.table}</td>
                                <td className="px-3 py-2">
                                  {r.status === 'success' ? (
                                    <span className="font-bold" style={{ color: '#5a9e6f' }}>✓ 成功</span>
                                  ) : (
                                    <span className="font-bold" style={{ color: '#cc4444' }}>✗ {r.error}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {r.newUrl && (
                                    <a href={r.newUrl} target="_blank" rel="nofollow noreferrer" className="underline hover:opacity-70" style={{ color: '#8b4513' }}>開く</a>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

        {/* Success Cases Tab Content */}
        {mainTab === 'success' && (
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #e8d8cc' }}>
              <h2 className="font-bold" style={{ color: '#4a3728', fontSize: '15px' }}>成婚事例一覧</h2>
              <span style={{ color: '#9b8b7f', fontSize: '13px' }}>{cases.length}件</span>
            </div>
            {casesLoading ? (
              <div className="flex items-center justify-center py-20 gap-3" style={{ color: '#9b8b7f' }}>
                <span className="flex items-center justify-center w-5 h-5">
                  <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#e8934e' }}></i>
                </span>
                読み込み中...
              </div>
            ) : (
              <SuccessCaseTable
                cases={cases}
                onEdit={handleCaseEdit}
                onDelete={handleCaseDelete}
                onStatusToggle={handleCaseStatusToggle}
                onReorder={handleCaseReorder}
              />
            )}
          </div>
        )}
      </div>

      {/* Blog Modal */}
      <BlogFormModal
        isOpen={blogModalOpen}
        onClose={() => setBlogModalOpen(false)}
        post={editPost}
        categories={categories}
        onSaved={handleBlogSaved}
      />

      {/* Success Case Modal */}
      <SuccessCaseFormModal
        isOpen={caseModalOpen}
        onClose={() => setCaseModalOpen(false)}
        item={editCase}
        onSaved={handleCaseSaved}
        nextSortOrder={nextCaseSortOrder}
      />
    </div>
  );
}